import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { X, Loader2, Save } from 'lucide-react';
import { useNotification } from '../contexts/NotificationContext';

const EditMentorModal = ({ isOpen, onClose, mentor, isAddMode, onMentorSaved }) => {
    const [formData, setFormData] = useState({
        name: '',
        role: '',
        company: '',
        companyLogo: '',
        workExp: '',
        teachingExp: '',
        image: '',
        bio: '',
        order: 0
    });

    // File states for Cloudinary upload
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);

    const [submitting, setSubmitting] = useState(false);
    const { showSuccess, showError } = useNotification();

    useEffect(() => {
        if (mentor) {
            setFormData({
                name: mentor.name || '',
                role: mentor.role || '',
                company: mentor.company || '',
                companyLogo: mentor.companyLogo || '',
                workExp: mentor.workExp || '',
                teachingExp: mentor.teachingExp || '',
                image: mentor.image || '',
                bio: mentor.bio || '',
                order: mentor.order || 0
            });
            setImagePreview(mentor.image || null);
            setLogoPreview(mentor.companyLogo || null);
            setImageFile(null);
            setLogoFile(null);
        } else {
            setFormData({
                name: '', role: '', company: '', companyLogo: '', workExp: '', teachingExp: '', image: '', bio: '', order: 0
            });
            setImagePreview(null);
            setLogoPreview(null);
            setImageFile(null);
            setLogoFile(null);
        }
    }, [mentor, isAddMode, isOpen]);

    const handleImageChange = (e, setFile, setPreview) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                showError('Image size must be less than 5MB');
                return;
            }
            setFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setSubmitting(true);

            // Construct FormData for multipart submission
            const submitData = new FormData();
            Object.keys(formData).forEach(key => {
                // Don't append empty optional fields
                if (formData[key] !== '' && formData[key] !== null && formData[key] !== undefined) {
                    submitData.append(key, formData[key]);
                }
            });

            // Append files if selected
            if (imageFile) submitData.append('image', imageFile);
            if (logoFile) submitData.append('companyLogo', logoFile);

            let response;
            if (isAddMode) {
                response = await api.post('/mentors', submitData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                showSuccess('Mentor created successfully');
            } else {
                response = await api.put(`/mentors/${mentor._id}`, submitData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                showSuccess('Mentor updated successfully');
            }

            onMentorSaved(response.data, isAddMode);
        } catch (error) {
            console.error('Failed to save mentor:', error);
            showError(error.response?.data?.message || 'Failed to save mentor');
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div
                className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50">
                    <h2 className="text-xl font-semibold text-gray-800">
                        {isAddMode ? 'Add New Mentor' : 'Edit Mentor'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 overflow-y-auto w-full custom-scrollbar">
                    <form id="mentorForm" onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Left Column */}
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none transition-all placeholder:text-gray-400"
                                        placeholder="e.g. John Doe"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                                    <input
                                        type="text"
                                        name="role"
                                        value={formData.role}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none transition-all placeholder:text-gray-400"
                                        placeholder="e.g. Senior DevOps Architect"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Work Exp</label>
                                        <input
                                            type="text"
                                            name="workExp"
                                            value={formData.workExp}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none transition-all placeholder:text-gray-400"
                                            placeholder="e.g. 12+ Years"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Teaching Exp</label>
                                        <input
                                            type="text"
                                            name="teachingExp"
                                            value={formData.teachingExp}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none transition-all placeholder:text-gray-400"
                                            placeholder="e.g. 5+ Years"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Right Column */}
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                                    <input
                                        type="text"
                                        name="company"
                                        value={formData.company}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none transition-all placeholder:text-gray-400"
                                        placeholder="e.g. Google"
                                    />
                                </div>

                                <div>
                                    <div className="flex justify-between items-end mb-1">
                                        <label className="block text-sm font-medium text-gray-700">Company Logo Upload</label>
                                        {logoPreview && (
                                            <div className="h-8 w-16 bg-gray-50 border border-gray-100 rounded flex items-center justify-center overflow-hidden flex-shrink-0 relative group">
                                                <img
                                                    src={logoPreview}
                                                    alt="Logo Preview"
                                                    className="h-full w-full object-contain p-0.5 group-hover:opacity-50 transition-opacity"
                                                    onError={(e) => { e.target.style.display = 'none'; }}
                                                />
                                                <div className="absolute inset-0 hidden group-hover:flex items-center justify-center pointer-events-none">
                                                    <span className="text-[10px] bg-black/50 text-white px-1 rounded">Change</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => handleImageChange(e, setLogoFile, setLogoPreview)}
                                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none hover:bg-gray-50 cursor-pointer file:cursor-pointer file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-700 file:font-medium file:px-3 file:py-1 file:mr-3"
                                    />
                                    <p className="text-xs text-gray-400 mt-1">Leaves URL unchanged if not selected</p>
                                </div>

                                <div>
                                    <div className="flex justify-between items-end mb-1">
                                        <label className="block text-sm font-medium text-gray-700">Profile Image Upload</label>
                                        {imagePreview && (
                                            <div className="h-8 w-8 rounded-full bg-gray-50 border border-gray-100 overflow-hidden flex-shrink-0 relative group">
                                                <img
                                                    src={imagePreview}
                                                    alt="Profile Preview"
                                                    className="h-full w-full object-cover group-hover:opacity-50 transition-opacity"
                                                    onError={(e) => { e.target.style.display = 'none'; }}
                                                />
                                                <div className="absolute inset-0 hidden group-hover:flex items-center justify-center pointer-events-none">
                                                    <span className="text-[10px] bg-black/50 text-white rounded">Edit</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => handleImageChange(e, setImageFile, setImagePreview)}
                                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none hover:bg-gray-50 cursor-pointer file:cursor-pointer file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-700 file:font-medium file:px-3 file:py-1 file:mr-3"
                                    />
                                    <p className="text-xs text-gray-400 mt-1">Leave empty to keep existing or use fallback</p>
                                </div>
                            </div>
                        </div>

                        {/* Full Width */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                            <textarea
                                name="bio"
                                value={formData.bio}
                                onChange={handleChange}
                                required
                                rows={3}
                                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none transition-all placeholder:text-gray-400 resize-none"
                                placeholder="Short biography..."
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
                            <input
                                type="number"
                                name="order"
                                value={formData.order}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none transition-all placeholder:text-gray-400"
                                placeholder="0"
                            />
                            <p className="text-xs text-gray-400 mt-1">Lower numbers appear first on the frontend.</p>
                        </div>
                    </form>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex flex-col-reverse sm:flex-row justify-end gap-3 mt-auto">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={submitting}
                        className="px-5 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg transition-colors focus:ring-2 focus:ring-gray-100 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        form="mentorForm"
                        type="submit"
                        disabled={submitting}
                        className="inline-flex items-center justify-center gap-2 px-6 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors focus:ring-2 focus:ring-blue-100 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm min-w-[120px]"
                    >
                        {submitting ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <span>Saving...</span>
                            </>
                        ) : (
                            <>
                                <Save className="w-4 h-4" />
                                <span>{isAddMode ? 'Create Mentor' : 'Save Changes'}</span>
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditMentorModal;
